import React, { useMemo } from "react";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";

import { selectLayerByVersionAndLayerKey, useGetFeaturesQuery } from "fond/api/layersSlice";
import { buildPathGraph } from "fond/draw/modes/base/pathGraph";
import MultiLayerEditPanelContent from "fond/draw/MultiLayerEdit/MultiLayerEditPanelContent";
import { MultiLayerEditProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProvider";
import { validateLoopConnection } from "fond/draw/validation/muPowerLoopConnectionValidation";
import { DeleteButton, PolygonSelectButton, RedoButton, SelectButton, ShortestPathButton, ToolbarConfig, UndoButton } from "fond/map/Toolbar";
import { MuPowerEditableLayerIds, MuPowerOutputLayerIds, MuPowerSolverLayerIds } from "fond/types";
import { isAnyLineString } from "fond/types/geojson";
import { useAppSelector } from "fond/utils/hooks";
import { BlockSpinner, useStackedNavigationContext } from "fond/widgets";

const toolbarConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />, <PolygonSelectButton key="selectPolygon" />],
  },
  {
    id: "drawTools",
    type: "mode",
    items: [<ShortestPathButton key="shortestPath" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

const WIRE_SIZES = ["3x_1/0_Al", "1/0_Al"];
const DEFAULT_WIRE_SIZE = "3x_1/0_Al";
const PHASE_OPTIONS_BY_WIRE_SIZE = {
  "3x_1/0_Al": ["ABC", "X"],
  "1/0_Al": ["A", "B", "C", "X"],
};
const DEFAULT_PHASE = "X";

const MuPowerLoopConnectionsDraw: React.FC = () => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { clear } = useStackedNavigationContext();

  const ugPathLayer = useAppSelector((state) => selectLayerByVersionAndLayerKey(state, { versionId, layerId: MuPowerEditableLayerIds.ugPath }));
  const { data: ugPathFeatures, isLoading: ugPathLoading } = useGetFeaturesQuery({ layerId: ugPathLayer?.ID ?? "" }, { skip: !ugPathLayer?.ID });

  const ugPathGraph = useMemo(() => {
    if (!ugPathFeatures) return undefined;
    return buildPathGraph(ugPathFeatures.filter(isAnyLineString), { splitIntersectingPaths: true });
  }, [ugPathFeatures]);

  const awaitingGraph = !!ugPathLayer?.ID && ugPathLoading;

  const options: MapboxDrawOptions = useMemo(
    () => ({
      snap: true,
      snapOptions: {
        snapVertexPriorityDistance: 0.000025,
      },
      shortestPath: ugPathGraph
        ? {
            graph: ugPathGraph,
            singleFeature: true,
            previewStyle: {
              path: { "line-color": "#000000", "line-width": 9 },
              tentative: { "line-color": "#000000", "line-width": 7, "line-dasharray": [1, 1] },
              node: { "circle-color": "#000000" },
            },
          }
        : undefined,
      editOptions: {
        allLayers: [
          {
            ID: MuPowerSolverLayerIds.loopConnections,
            GeometryType: "LineString",
            getFeatureProperties: () => ({
              layerId: MuPowerSolverLayerIds.loopConnections,
              WireSize: DEFAULT_WIRE_SIZE,
              Phase: DEFAULT_PHASE,
            }),
            validate: validateLoopConnection,
            Attributes: {
              WireSize: { Type: "ENUM", Options: WIRE_SIZES },
              Phase: {
                Type: "DEPENDENT_ENUM",
                DependsOn: "WireSize",
                OptionsMap: PHASE_OPTIONS_BY_WIRE_SIZE,
                DefaultValue: DEFAULT_PHASE,
              },
            },
            PromptAttributesOnCreate: true,
          },
          { ID: MuPowerSolverLayerIds.sourceHubs, GeometryType: "Point", getFeatureProperties: () => ({}), Hide: true },
          { ID: MuPowerSolverLayerIds.transformerHubs, GeometryType: "Point", getFeatureProperties: () => ({}), Hide: true },
          { ID: MuPowerOutputLayerIds.boxes, GeometryType: "Point", getFeatureProperties: () => ({}), Hide: true },
        ],
        editingLayerId: MuPowerSolverLayerIds.loopConnections,
        editingLayerIds: [MuPowerSolverLayerIds.loopConnections],
      },
    }),
    [ugPathGraph]
  );

  if (awaitingGraph) {
    return <BlockSpinner />;
  }

  return (
    <MultiLayerEditProvider versionId={versionId} options={options} config={toolbarConfig}>
      <MultiLayerEditPanelContent title="Edit loop connections" alertTitle="Draw loop connections" onFinish={clear} />
    </MultiLayerEditProvider>
  );
};

export default MuPowerLoopConnectionsDraw;
